/**
 * CalendarPage — 캘린더 모듈 메인 라우트 컴포넌트
 *
 * - 헤더: 캘린더 + 네비 + 뷰 picker + [알림 🔔] + [전사 일정 admin] + [새 일정]
 * - 진입 시 unread invitation 안내 토스트 1회 (확인/닫기 어느 쪽이든 그 이후엔 안 뜸)
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { useSearchParams } from 'react-router-dom';
import { useCalendarContext } from '../../context/CalendarContext';
import {
  CalendarEvent, CalendarViewMode, categorizeEvent, eventChipStyle, chipMarker,
  isChannelEnabled,
} from '../../models/CalendarEvent';
import { My } from '../../services/My';
import { fetchEvent } from '../../services/CalendarService';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { expandOccurrences } from './recurrence';
import { useEventReminders } from './useEventReminders';
import { useHolidays } from './useHolidays';
import { useCalendarLabels } from './useCalendarLabels';
import { useAppUIContext } from '../../context/AppUIContext';
import { MenuToggleButton } from '../components/MenuToggleButton';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { cn } from '../../lib/utils';
import {
  CalendarDays, PenLine, ChevronLeft, ChevronRight,
  LayoutGrid, Rows2, List, Calendar as DayIcon, Building2,
} from 'lucide-react';
import MonthView from './views/MonthView';
import AgendaView from './views/AgendaView';
import WeekView from './views/WeekView';
import DayView from './views/DayView';
import EventCreateModal from './EventCreateModal';
import EventDetailOverlay from './EventDetailOverlay';
import TaskPreviewDialog from './TaskPreviewDialog';

const VIEW_OPTIONS: { value: CalendarViewMode; icon: React.ElementType }[] = [
  { value: 'month',  icon: LayoutGrid },
  { value: 'week',   icon: Rows2 },
  { value: 'day',    icon: DayIcon },
  { value: 'agenda', icon: List },
];

function formatRangeLabel(mode: CalendarViewMode, anchor: Date): string {
  const loc = i18n.locale || 'en';
  switch (mode) {
    case 'month': return anchor.toLocaleDateString(loc, { year: 'numeric', month: 'long' });
    case 'week': {
      const day = anchor.getDay();
      const start = new Date(anchor);
      start.setDate(start.getDate() - day);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const f = { month: 'short', day: 'numeric' } as const;
      return `${start.toLocaleDateString(loc, f)} – ${end.toLocaleDateString(loc, f)}`;
    }
    case 'day': return anchor.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
    case 'agenda': return anchor.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' });
  }
}

/**
 * 일정 필터링 — 3 분류 (전사/채널/개인) + 시스템.
 *
 * 우선순위:
 *   1. __holiday → holidays
 *   2. __task   → showChannelTasks && channels[task.channel_id]
 *   3. visibility=public_workspace → workspace
 *   4. owner_id==me && owner_channel_id=null → personal (공유 여부 무관 — 본인 거)
 *   5. owner_channel_id ∈ myChannels → channels[owner_channel_id]
 *   6. shared_channel_ids 중 사용자 채널 매칭 (= 다른 사람의 공개 일정)
 *      → showSharedEvents && channels[matched]
 *   7. 그 외 → false
 */
function shouldShowEvent(
  event: CalendarEvent,
  filters: ReturnType<typeof useCalendarContext>['filters'],
  myUserId: string,
  myChannelIds: Set<string>,
  isAttendee: boolean,
): boolean {
  if (event.metadata?.__holiday) return filters.holidays;
  if (event.metadata?.__task) {
    if (!filters.showChannelTasks) return false;
    const cid = event.metadata.channel_id as string | undefined;
    if (!cid) return false;
    return isChannelEnabled(filters, cid);
  }
  if (event.visibility === 'public_workspace') return filters.workspace;

  // 본인의 개인 일정 — 공유 여부 무관 (본인 작성이라 personal 그룹)
  if (event.owner_id === myUserId && !event.owner_channel_id) {
    return filters.personal;
  }

  // 채널 owner 일정
  if (event.owner_channel_id && myChannelIds.has(event.owner_channel_id)) {
    return isChannelEnabled(filters, event.owner_channel_id);
  }

  // 다른 사람의 공개된 일정 (그 사람의 개인 일정을 내 채널에 공유) — 채널 토글 종속
  for (const cid of event.shared_channel_ids) {
    if (myChannelIds.has(cid)) {
      if (!filters.showSharedEvents) return false;
      return isChannelEnabled(filters, cid);
    }
  }

  // 초대받은 일정 (attendee) — 위 분류에 안 걸리는 타인 일정. 개인 관심사라 personal 토글에 묶음
  if (isAttendee) return filters.personal;

  return false;
}

/**
 * 반복 일정의 인스턴스 전개 — recurrence_rule 있는 원본 1개를 N개의 가상 이벤트로 펼침.
 * 가상 이벤트의 id 는 "<originalId>::<recurrenceISO>" 형태 — 클릭 시 원본 lookup 가능.
 */
function expandRecurringEvents(
  events: CalendarEvent[],
  rangeStart: Date,
  rangeEnd: Date,
): CalendarEvent[] {
  const result: CalendarEvent[] = [];
  for (const ev of events) {
    if (!ev.recurrence_rule) {
      result.push(ev);
      continue;
    }
    const dtstart = new Date(ev.start_at);
    const durationMs = new Date(ev.end_at).getTime() - dtstart.getTime();
    const occurrences = expandOccurrences({
      rrule: ev.recurrence_rule,
      dtstart,
      rangeStart,
      rangeEnd,
    });
    for (const occ of occurrences) {
      result.push({
        ...ev,
        id: `${ev.id}::${occ.toISOString()}`,
        start_at: occ.toISOString(),
        end_at: new Date(occ.getTime() + durationMs).toISOString(),
      });
    }
  }
  return result;
}

const CalendarPage: React.FC = () => {
  const {
    viewMode, setViewMode, anchorDate, setAnchorDate, range, events, taskEvents, loading, error,
    selectedEvent, setSelectedEvent, filters, attendingEventIds, remindersMap,
  } = useCalendarContext();
  const myRole = useMyRole();
  const { activeWorkspaceId, rooms } = useAppUIContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // 본인이 속한 채널 집합 (1:1 제외)
  const myChannelIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rooms) {
      if (r.workspaceId === activeWorkspaceId
        && (r.visibility === 'listed' || r.visibility === 'unlisted')
        && !r.isOneToOne) {
        set.add(r.id);
      }
    }
    return set;
  }, [rooms, activeWorkspaceId]);

  // 일정 → 시각 스타일 헬퍼 (categorizeEvent + eventChipStyle + 강조 마커 + 번역 라벨)
  const labels = useCalendarLabels();
  const getStyle = useCallback((event: CalendarEvent) => {
    const category = categorizeEvent(
      event,
      My.user.id,
      myChannelIds,
      attendingEventIds.has(event.id),
      undefined, // 메인 캘린더에선 currentChannelId 없음
    );
    return { ...eventChipStyle(category), marker: chipMarker(category), label: labels.category(category) };
  }, [myChannelIds, attendingEventIds, labels]);

  // 공휴일 가상 일정 — workspace 설정 ∩ 멤버 선택 + filters.holidays 토글로 on/off
  const { events: holidayEvents } = useHolidays({
    workspaceId: activeWorkspaceId ?? null,
    rangeStart: range.start,
    rangeEnd: range.end,
    enabled: filters.holidays,
    selectedCountries: filters.holidayCountries,
  });

  const [showCreate, setShowCreate] = useState(false);
  const detailAnchorRef = useRef<HTMLElement | null>(null); // 상세 팝오버 앵커 (클릭한 칩)
  const [createInitialDate, setCreateInitialDate] = useState<Date | null>(null);
  const [forceWorkspace, setForceWorkspace] = useState(false);
  const [taskPreview, setTaskPreview] = useState<CalendarEvent | null>(null);

  /** 클릭 분기 — task 가상 이벤트는 TaskPreviewDialog, 일반 이벤트는 EventDetailOverlay */
  const handleEventClick = useCallback((ev: CalendarEvent, e?: React.MouseEvent) => {
    if (ev.metadata?.__task) {
      setTaskPreview(ev);
      return;
    }
    // 클릭한 마우스 좌표를 0-size 가상 앵커로 → 팝오버가 마우스 바로 아래에 표시
    if (e) {
      const x = e.clientX, y = e.clientY;
      detailAnchorRef.current = { getBoundingClientRect: () => new DOMRect(x, y, 0, 0) } as unknown as HTMLElement;
    }
    const originalId = ev.id.split('::')[0];
    const original = events.find((ev2) => ev2.id === originalId);
    setSelectedEvent(original ?? ev);
  }, [events, setSelectedEvent]);

  /** 푸시/딥링크 진입 (?event=ID) → 해당 일정 상세 자동 오픈 */
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (!eventId) return;
    let cancelled = false;
    fetchEvent(eventId).then((ev) => {
      if (!cancelled && ev) setSelectedEvent(ev);
      const next = new URLSearchParams(searchParams);
      next.delete('event');
      setSearchParams(next, { replace: true });
    }).catch(() => { /* 조회 실패는 무시 — 캘린더 화면 유지 */ });
    return () => { cancelled = true; };
  }, [searchParams, setSelectedEvent, setSearchParams]);

  const isAdmin = myRole === 'owner' || myRole === 'admin';
  const isGuest = myRole === 'guest';

  const rangeLabel = useMemo(() => formatRangeLabel(viewMode, anchorDate), [viewMode, anchorDate]);

  /** 반복 일정 인스턴스 전개 + 공휴일 + Tasks 마감일 → shouldShowEvent 일괄 필터 */
  const filteredEvents = useMemo(() => {
    const expanded = expandRecurringEvents(events, range.start, range.end);
    const merged = [...expanded, ...holidayEvents, ...taskEvents];
    return merged.filter((e) => shouldShowEvent(e, filters, My.user.id, myChannelIds, attendingEventIds.has(e.id.split('::')[0])));
  }, [events, taskEvents, range.start.getTime(), range.end.getTime(), filters, myChannelIds, holidayEvents, attendingEventIds]);

  // in-browser 리마인더 (Phase 1 경량). Phase 2 에 FCM 푸시로 교체.
  useEventReminders(filteredEvents, remindersMap);

  const handlePrev = () => {
    const next = new Date(anchorDate);
    switch (viewMode) {
      case 'month': next.setMonth(next.getMonth() - 1); break;
      case 'week':  next.setDate(next.getDate() - 7); break;
      default:      next.setDate(next.getDate() - 1); break;
    }
    setAnchorDate(next);
  };

  const handleNext = () => {
    const next = new Date(anchorDate);
    switch (viewMode) {
      case 'month': next.setMonth(next.getMonth() + 1); break;
      case 'week':  next.setDate(next.getDate() + 7); break;
      default:      next.setDate(next.getDate() + 1); break;
    }
    setAnchorDate(next);
  };

  const handleToday = () => setAnchorDate(new Date());

  return (
    <div className="flex flex-col h-full bg-background rounded-panel-mobile lg:rounded-panel overflow-hidden relative">
      {/* ── Header ── */}
      <header className="flex flex-col lg:flex-row lg:items-center gap-1.5 px-3 lg:px-4 py-2 lg:py-0 lg:h-14 shrink-0 border-b border-border">
        {/* 1행: 메뉴 + 제목 + 네비 + 기간 라벨 */}
        <div className="flex items-center gap-1.5 min-w-0">
          <MenuToggleButton />
          <CalendarDays className="size-5 text-primary shrink-0" strokeWidth={2} />
          <h1 className="text-body-lg font-semibold text-foreground truncate hidden lg:block"><Trans>Calendar</Trans></h1>

          <div className="flex items-center gap-0.5 shrink-0">
            <button
              onClick={handlePrev}
              className="size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label={t`Previous`}
            ><ChevronLeft className="size-4" /></button>
            <button
              onClick={handleToday}
              className="h-8 px-2 rounded-lg text-label font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            ><Trans>Today</Trans></button>
            <button
              onClick={handleNext}
              className="size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label={t`Next`}
            ><ChevronRight className="size-4" /></button>
          </div>

          <span className="text-label text-muted-foreground flex-1 min-w-0 truncate ml-1">{rangeLabel}</span>
        </div>

        {/* 2행(모바일) / 우측(PC): 뷰 선택 + 생성 액션 */}
        <div className="flex items-center gap-1 shrink-0 lg:ml-auto">
          <Select value={viewMode} onValueChange={(v) => setViewMode(v as CalendarViewMode)}>
            <SelectTrigger className="h-8 w-auto gap-1.5 px-2.5 text-label whitespace-nowrap">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {VIEW_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                return (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex items-center gap-1.5">
                      <Icon className="size-3.5" />
                      {labels.viewMode(opt.value)}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* 모바일: viewMode 는 좌측 고정, 생성 버튼들(전사+개인)은 우측에 함께 묶음 */}
          <div className="flex-1 lg:hidden" />

          {/* 전사 일정 admin 버튼 — 개인 일정 버튼 바로 왼쪽. 모바일은 아이콘만 (덜 빈번 + admin 전용) */}
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 px-2 sm:px-3 border-admin/40 text-admin bg-admin-bg hover:bg-admin/15 hover:text-admin"
              onClick={() => { setCreateInitialDate(null); setForceWorkspace(true); setShowCreate(true); }}
              title={t`Add workspace event`}
            >
              <Building2 className="size-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline"><Trans>Workspace event</Trans></span>
            </Button>
          )}

          {/* 새 일정 — 모바일은 아이콘만, PC 는 전체 라벨 */}
          {!isGuest && (
            <Button
              size="sm"
              className="shrink-0 px-2 sm:px-3"
              onClick={() => { setCreateInitialDate(null); setForceWorkspace(false); setShowCreate(true); }}
              title={t`Create personal event (My calendar)`}
            >
              <PenLine className="size-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline"><Trans>New personal event</Trans></span>
            </Button>
          )}
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex-1 overflow-hidden min-h-0">
        {error && (
          <div className="flex items-center justify-center h-full text-caption text-destructive">{error}</div>
        )}
        {!error && viewMode === 'month' && (
          <MonthView
            anchorDate={anchorDate}
            events={filteredEvents}
            onEventClick={handleEventClick}
            onCellClick={isGuest ? undefined : (date) => {
              setCreateInitialDate(date); setForceWorkspace(false); setShowCreate(true);
            }}
            getStyle={getStyle}
          />
        )}
        {!error && viewMode === 'agenda' && (
          <AgendaView
            anchorDate={anchorDate}
            events={filteredEvents}
            loading={loading}
            onEventClick={handleEventClick}
            getStyle={getStyle}
          />
        )}
        {!error && viewMode === 'week' && (
          <WeekView
            anchorDate={anchorDate}
            events={filteredEvents}
            onEventClick={handleEventClick}
            onCellClick={isGuest ? undefined : (date) => {
              setCreateInitialDate(date); setForceWorkspace(false); setShowCreate(true);
            }}
            getStyle={getStyle}
          />
        )}
        {!error && viewMode === 'day' && (
          <DayView
            anchorDate={anchorDate}
            events={filteredEvents}
            onEventClick={handleEventClick}
            onCellClick={isGuest ? undefined : (date) => {
              setCreateInitialDate(date); setForceWorkspace(false); setShowCreate(true);
            }}
            getStyle={getStyle}
          />
        )}
      </div>

      {/* 모달들 */}
      <EventCreateModal
        open={showCreate}
        onOpenChange={setShowCreate}
        initialDate={createInitialDate}
        forceWorkspace={forceWorkspace}
      />

      <EventDetailOverlay
        open={!!selectedEvent}
        onOpenChange={(v) => { if (!v) setSelectedEvent(null); }}
        event={selectedEvent}
        anchorRef={detailAnchorRef}
      />

      <TaskPreviewDialog
        open={!!taskPreview}
        onOpenChange={(o) => { if (!o) setTaskPreview(null); }}
        event={taskPreview}
      />
    </div>
  );
};

export default CalendarPage;
